import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import indexedDBStorage from "./indexedDBStorage";
import { membershipService } from "../services";
/**
 * @import { App.Store.User, App.Store.Profile } from '../typedef.js'
 */

/**
 * Converts role code to a human-readable string.
 * @param {string} role
 * @return {string}
 */
export const roleToString = (role) => {
  switch (role) {
    case "A":
      return "Super Admin";
    case "CA":
      return "Community Admin";
    case "GL":
      return "Group Leader";
    case "C":
      return "Collector";
    case "POCA":
      return "POC Attendant";
    case "U":
      return "User";
    default:
      return "Unknown";
  }
};

/**
 * Zustand store for user state management.
 */
const useUserStore2 = create(
  persist(
    immer((set) => ({
      /** @type {App.Store.User | null} */
      user: null,

      /**
       * Converts API data to App.Store.User type.
       * @return {App.Store.User}
       */
      apiDataToUser: (data) => {
        return {
          id: data._id,
          firstName: data.first_name,
          lastName: data.last_name,
          name: data.name,
          status: data.status,
          role: roleToString(data.role),
          nric: data.nric,
          approved: data.approved,
          type: data.type,
          profilePicture: data.medias?.[0]?.img_url || null, // Get profile picture from medias array
          createDate: data.create_date
            ? new Date(data.create_date).toLocaleDateString()
            : "Not Available",
          lastLogin: data.last_login
            ? new Date(data.last_login).toLocaleString()
            : "Not Available",
          lastUpdate: data.last_update
            ? new Date(data.last_update).toLocaleString()
            : "Not Available",
          email: data.email,
          phone: data.phone,
          primaryProfile: {
            id: data.primary_profile_id?._id ?? null,
            name: data.primary_profile_id?.name ?? null,
            type: data.primary_profile_id?.type ?? null,
            status: data.primary_profile_id?.status ?? null,
            createDate: data.primary_profile_id?.create_date ?? null,
            lastUpdate: data.primary_profile_id?.last_update ?? null,
            profilePicture:
              data.primary_profile_id?.medias?.[0]?.img_url || null, // Primary profile picture
          },
        };
      },

      /**
       * Set or update the user object.
       * @param {App.Store.User} userData - New user data.
       */
      setUser: (userData) =>
        set((state) => {
          state.user = {
            ...userData,
            profiles: userData.profiles || [], // Ensure profiles exist
            memberships: userData.memberships || [],
          };
        }),

      /**
       * Partially update user properties while keeping others unchanged.
       * @param {Partial<App.Store.User>} updates - The user properties to update.
       */
      updateUser: (updates) =>
        set((state) => {
          if (state.user) {
            for (const key in updates) {
              state.user[key] = updates[key];
            }
          }
        }),

      /**
       * Update the profile array.
       * @param {App.Store.Profile[]} profiles - Array of profiles.
       */
      updateProfiles: (profiles) =>
        set((state) => {
          if (state.user) {
            state.user.profiles = profiles;
          }
        }),

      //Updating the profile picture
      updateProfilePicture: (newPictureUrl) =>
        set((state) => {
          if (state.user) {
            state.user.profilePicture = newPictureUrl;
          }
        }),

      /**
       * Update the current profile of the user.
       * @param {App.Store.Profile} profile - Profile data.
       */
      setCurrentProfile: (profile) =>
        set((state) => {
          if (state.user) {
            state.user.currentProfile = profile;
          }
        }),

      fetchMemberships: async (profileId) =>
        set(async (state) => {
          if (state.user) {
            console.log(
              "userStore2 fetchMemberships - userId:",
              useUserStore2.getState().user.id
            );
            console.log("userStore2 fetchMemberships - profileId:", profileId);
            try {
              const memberships = await membershipService.getMembershipById(
                useUserStore2.getState().user.id,
                profileId
              );
              console.log(
                "userStore2 fetchMemberships - memberships:",
                memberships
              );

              // Find the index of the matching profile id in state.user.profiles with profileId
              const profileIndex = state.user.profiles.findIndex(
                (profile) => profile.id === profileId
              );
              state.user.profiles[profileIndex].memberships = memberships;
            } catch (error) {
              console.error("Error fetching memberships:", error);
            }
          }
        }),

      /**
       * Set profile role in the user state.
       * @param {string} role - Role assigned to the user in the profile.
       */
      setProfileRole: (role) =>
        set((state) => {
          if (state.user) {
            state.user.profileRole = role;
          }
        }),

      /**
       * Set community and group ID (or null if not provided).
       * @param {string | null} communityId - Community ID.
       * @param {string | null} groupId - Group ID.
       */
      setCommunityAndGroup: (communityId, groupId) =>
        set((state) => {
          if (state.user) {
            state.user.communityId = communityId ?? null;
            state.user.groupId = groupId ?? null;
          }
        }),

      /**
       * Set membership info into store. If memberships exist, overwrite.
       * @param {App.Store.Membership[]} memberships - Array of memberships.
       */
      setMemberships: (memberships) =>
        set((state) => {
          if (state.user) {
            state.user.memberships = memberships;
          }
        }),

      /**
       * Add membership info into store (append to existing memberships)
       * @param {App.Store.Membership[]} memberships - Array of memberships.
       */
      addMemberships: (memberships) =>
        set((state) => {
          console.log("userStore2 addMemberships - memberships:", memberships);
          if (state.user) {
            state.user.memberships = [
              ...(state.user.memberships || []),
              ...memberships,
            ];
          }
        }),

      /**
       * Reset user state to null (logout).
       */
      logout: () =>
        set((state) => {
          state.user = null;
        }),
    })),
    {
      name: "user-storage2",
      storage: indexedDBStorage,
      partialize: (state) => ({ user: state.user }),
    }
  )
);

// Subscribe to log state on every update (logs changes even if done outside of functions)
useUserStore2.subscribe((state) => {
  // console.log("User Store2 Updated:", state);
});

export default useUserStore2;
